import { z } from 'zod';
import type { PortalConfig } from '../config/schema.js';
import { INTEREST_VALUES, MARKET_CODES, ROLE_VALUES } from '../vocabulary.js';
import {
  CONTACT_FORM_FIELDS,
  FIELD_LABELS,
  LEAD_CREATION_LABELS,
  LEAD_CREATION_REQUIRED_FIELDS,
  type ContactFormField,
  type LeadCreationField,
} from './fields.js';

/**
 * Contact and lead creation validation (brief sections 2, 3, 6.1 and 6.7).
 * The same functions run in the browser and on the server.
 */

export type ValidationCode = 'required' | 'invalid' | 'unverified';

export interface ValidationError {
  field: ContactFormField;
  code: ValidationCode;
  message: string;
}

export type ValidationResult<T> = { ok: true; value: T } | { ok: false; errors: ValidationError[] };

/** A problem with one of the four lead creation answers. */
export interface LeadCreationError {
  field: LeadCreationField;
  code: ValidationCode;
  message: string;
}

export type LeadCreationResult =
  { ok: true; value: LeadCreationValues } | { ok: false; errors: LeadCreationError[] };

const REQUIRED = 'required';
const INVALID = 'invalid';
const requiredOrInvalid = (issue: { input?: unknown }) =>
  issue.input === undefined ? REQUIRED : INVALID;

/** Form inputs send "" for an empty field; treat blanks and null as "not given". */
function blankToUndefined(value: unknown): unknown {
  if (value === null) return undefined;
  if (typeof value === 'string') return value.trim() === '' ? undefined : value.trim();
  return value;
}

const requiredText = (max: number) =>
  z.preprocess(
    blankToUndefined,
    z.string({ error: requiredOrInvalid }).max(max, { error: INVALID }),
  );

const optionalText = (max: number) =>
  z.preprocess(
    blankToUndefined,
    z.string({ error: INVALID }).max(max, { error: INVALID }).optional(),
  );

const email = z.preprocess(
  blankToUndefined,
  z.email({ error: requiredOrInvalid }).max(254, { error: INVALID }),
);

/** The scan reads LinkedIn without a scheme at times; only http(s) links are kept. */
const linkedinUrl = z.preprocess(
  (value) => {
    const v = blankToUndefined(value);
    return typeof v === 'string' && !/^[a-z]+:/i.test(v) ? `https://${v}` : v;
  },
  z
    .string({ error: INVALID })
    .max(300, { error: INVALID })
    .refine(isHttpUrl, { error: INVALID })
    .optional(),
);

function isHttpUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return url.protocol === 'https:' || url.protocol === 'http:';
  } catch {
    return false;
  }
}

const marketList = z.preprocess(
  (value) => (value == null ? [] : value),
  z
    .array(z.enum(MARKET_CODES, { error: INVALID }), { error: INVALID })
    .refine((list) => new Set(list).size === list.length, { error: INVALID }),
);

export function createValidators(lists: Pick<PortalConfig['lists'], 'countries'>) {
  const countries = new Set(lists.countries);
  const countryName = z
    .string({ error: requiredOrInvalid })
    .refine((c) => countries.has(c), { error: INVALID });
  const interestValue = z.enum(INTEREST_VALUES, { error: requiredOrInvalid });
  const roleValue = z.enum(ROLE_VALUES, { error: requiredOrInvalid });
  // Required on a lead; on a contact, a blank answer is "not given".
  const country = z.preprocess(blankToUndefined, countryName);
  const role = z.preprocess(blankToUndefined, roleValue);
  const optional = <T extends z.ZodType>(schema: T) =>
    z.preprocess(blankToUndefined, schema.optional());

  const contactSchema = z.object({
    first_name: requiredText(100),
    last_name: requiredText(100),
    email,
    company: requiredText(200),
    job_title: optionalText(200),
    mobile: optionalText(50),
    office_phone: optionalText(50),
    linkedin_url: linkedinUrl,
    country: optional(countryName),
    applications: marketList,
    interest: optional(interestValue),
    role: optional(roleValue),
    pain: optionalText(2000),
    wants_content: z.preprocess((v) => v ?? false, z.boolean({ error: INVALID })),
    note: optionalText(5000),
  });

  // The installation country of a lead created before Nov 2, 2026 (or
  // waiting on a device since) may be missing: its company country stands in.
  const leadCreationSchema = z.preprocess(
    (input) => {
      if (input === null || typeof input !== 'object') return input;
      const v = input as Record<string, unknown>;
      return { ...v, installation_country: blankToUndefined(v.installation_country) ?? v.country };
    },
    z.object({
      role,
      applications: marketList.refine((list) => list.length > 0, { error: REQUIRED }),
      installation_country: country,
      pain: requiredText(2000),
    }),
  );

  /** Validates the contact form (Add contact, Hand the tablet, Edit contact). */
  function validateContact(input: unknown): ValidationResult<ContactValues> {
    return toResult(contactSchema.safeParse(input));
  }

  /** Validates the four lead creation answers (Scan, Add contact, Add lead, + Lead, Edit lead). */
  function validateLeadCreation(input: unknown): LeadCreationResult {
    const parsed = leadCreationSchema.safeParse(input);
    if (parsed.success) return { ok: true, value: parsed.data };
    const errors: LeadCreationError[] = [];
    for (const issue of parsed.error.issues) {
      const field = issue.path[0];
      if (typeof field !== 'string' || !isLeadField(field)) continue;
      if (errors.some((e) => e.field === field)) continue;
      const code: ValidationCode = issue.message === REQUIRED ? 'required' : 'invalid';
      errors.push({ field, code, message: leadMessageFor(field, code) });
    }
    return { ok: false, errors };
  }

  /**
   * What keeps a contact out of HubSpot (brief section 2 rule 9): a missing or
   * invalid required field, or a scan field not checked yet.
   */
  function contactPushBlockers(
    contact: Record<string, unknown> & { needs_verification?: readonly string[] | null },
  ): ValidationError[] {
    const result = validateContact(contact);
    const blockers = result.ok ? [] : [...result.errors];
    for (const field of contact.needs_verification ?? []) {
      if (!isFormField(field) || blockers.some((b) => b.field === field)) continue;
      blockers.push({
        field,
        code: 'unverified',
        message: `${FIELD_LABELS[field]}: check what the scan read.`,
      });
    }
    return blockers;
  }

  return { validateContact, validateLeadCreation, contactPushBlockers };
}

export type Validators = ReturnType<typeof createValidators>;

export interface ContactValues {
  first_name: string;
  last_name: string;
  email: string;
  company: string;
  job_title?: string | undefined;
  mobile?: string | undefined;
  office_phone?: string | undefined;
  linkedin_url?: string | undefined;
  country?: string | undefined;
  applications: (typeof MARKET_CODES)[number][];
  interest?: (typeof INTEREST_VALUES)[number] | undefined;
  role?: (typeof ROLE_VALUES)[number] | undefined;
  pain?: string | undefined;
  wants_content: boolean;
  note?: string | undefined;
}

export interface LeadCreationValues {
  role: (typeof ROLE_VALUES)[number];
  applications: (typeof MARKET_CODES)[number][];
  installation_country: string;
  pain: string;
}

function isLeadField(field: string): field is LeadCreationField {
  return (LEAD_CREATION_REQUIRED_FIELDS as readonly string[]).includes(field);
}

function leadMessageFor(field: LeadCreationField, code: ValidationCode): string {
  if (code === 'required') {
    return field === 'applications'
      ? 'Choose at least one application.'
      : `${LEAD_CREATION_LABELS[field]} is required.`;
  }
  return `${LEAD_CREATION_LABELS[field]} is not valid.`;
}

function isFormField(field: string): field is ContactFormField {
  return (CONTACT_FORM_FIELDS as readonly string[]).includes(field);
}

function messageFor(field: ContactFormField, code: ValidationCode): string {
  if (code === 'required') {
    return field === 'applications'
      ? 'Choose at least one application.'
      : `${FIELD_LABELS[field]} is required.`;
  }
  if (field === 'email') return 'Enter a valid email address.';
  return `${FIELD_LABELS[field]} is not valid.`;
}

function toResult<T>(parsed: z.ZodSafeParseResult<T>): ValidationResult<T> {
  if (parsed.success) return { ok: true, value: parsed.data };
  const errors: ValidationError[] = [];
  for (const issue of parsed.error.issues) {
    const field = issue.path[0];
    if (typeof field !== 'string' || !isFormField(field)) continue;
    if (errors.some((e) => e.field === field)) continue; // first problem per field only
    const code: ValidationCode = issue.message === REQUIRED ? 'required' : 'invalid';
    errors.push({ field, code, message: messageFor(field, code) });
  }
  return { ok: false, errors };
}
